import React from 'react';
import { motion } from 'framer-motion';
import { FileCheck, Shield, Target, AlertTriangle, ClipboardCheck, Scale, BookOpen, LineChart, CheckCircle, ArrowRight } from 'lucide-react';

interface ComplianceRiskProps {
  setCurrentPage: (page: string) => void;
}

const ComplianceRisk: React.FC<ComplianceRiskProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "Risk Assessment",
      description: "Comprehensive evaluation of security risks and compliance gaps.",
      items: [
        "Vulnerability assessment",
        "Risk analysis",
        "Impact evaluation",
        "Mitigation planning"
      ]
    },
    {
      icon: Target,
      title: "Compliance Monitoring",
      description: "Continuous monitoring of regulatory requirements and standards.",
      items: [
        "Real-time monitoring",
        "Compliance tracking",
        "Policy enforcement",
        "Audit readiness"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Risk Management",
      description: "Proactive identification and mitigation of security risks.",
      items: [
        "Risk identification",
        "Control implementation",
        "Risk mitigation",
        "Continuous monitoring"
      ]
    },
    {
      icon: ClipboardCheck,
      title: "Audit Management",
      description: "Streamlined audit processes and documentation management.",
      items: [
        "Audit preparation",
        "Documentation control",
        "Evidence collection",
        "Audit support"
      ]
    }
  ];

  const services = [
    {
      icon: Scale,
      title: "Regulatory Compliance",
      description: "Ensure adherence to industry regulations and standards.",
      items: [
        "GDPR compliance",
        "HIPAA requirements",
        "PCI DSS standards",
        "Industry regulations"
      ]
    },
    {
      icon: BookOpen,
      title: "Policy Management",
      description: "Develop and maintain effective security policies.",
      items: [
        "Policy development",
        "Procedure creation",
        "Standard implementation",
        "Policy review"
      ]
    },
    {
      icon: LineChart,
      title: "Risk Analytics",
      description: "Advanced analytics for risk assessment and monitoring.",
      items: [
        "Risk metrics",
        "Trend analysis",
        "Predictive analytics",
        "Risk reporting"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Shield,
      title: "Compliance Framework",
      items: [
        "Regulatory mapping",
        "Control implementation",
        "Gap analysis",
        "Compliance reporting"
      ]
    },
    {
      icon: Target,
      title: "Risk Assessment",
      items: [
        "Threat assessment",
        "Vulnerability scanning",
        "Impact analysis",
        "Risk scoring"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Incident Management",
      items: [
        "Incident response",
        "Investigation",
        "Documentation",
        "Lessons learned"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <FileCheck className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Compliance & Risk Management</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Comprehensive compliance solutions and risk management strategies to protect your organization and meet regulatory requirements
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Compliance Services</h2>
            <p className="text-lg text-muted">
              Comprehensive compliance and risk management services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{service.description}</p>

                <ul className="space-y-3">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Core Capabilities</h2>
            <p className="text-lg text-muted">
              Advanced capabilities for comprehensive compliance and risk management
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>

                <ul className="space-y-3">
                  {capability.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Enhance Your Compliance?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Get started with our compliance and risk management services to protect your organization and meet regulatory requirements
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ComplianceRisk;